<template>
  <div :id="`stackedbar-wrapper-${chartId}`">
    <svg :id="`stackedbar-${chartId}`"></svg>
    <div class="chart-tooltip">
      <p class="font-weight-medium text-legend mb-10">
        {{ this.question }}
      </p>
      <p class="text-small mb-0">Country: {{ this.country }}</p>
      <p class="text-small mb-0">{{ selectedBarSection.answer }}: {{ selectedBarSection.percentage }}%</p>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import kebabCase from 'lodash-es/kebabCase';

export default {
  name: 'StackedBarChart',
  props: {
    question: {
      type: String,
      required: true,
    },
    country: {
      type: String,
      required: true,
    },
  },
  watch: {
    question(newQn) {
      this.formatData(newQn);
      this.updateChart();
    },
  },
  computed: {
    chartId() {
      return kebabCase(this.country);
    },
    chartDim() {
      if (this.$breakpoint.mobile) {
        return {
          width: 450,
          height: 48,
          barHeight: 45,
          marginX: 15,
          arc: 6,
        };
      }
      return {
        width: 450,
        height: 40,
        barHeight: 35,
        marginX: 15,
        arc: 6,
      };
    },
  },
  data() {
    return {
      cleanData: null,
      rawData: null,
      chart: null,
      dataSrc: `${process.env.BASE_URL}data/stacked-bar-chart-data.csv`,
      selectedBarSection: {
        answer: 'Yes',
        percentage: 50,
      },
      colors: {
        Yes: { bar: '#005EB8', label: '#FFFFFF' },
        No: { bar: '#FDC9D7', label: '#373839' },
        'Every day': { bar: '#004A91', label: '#FFFFFF' },
        'Several times a week': { bar: '#005EB8', label: '#FFFFFF' },
        'Once a week': { bar: '#437CC7', label: '#FFFFFF' },
        'Once in two weeks': { bar: '#6BA0E0', label: '#1F1F1F' },
        'Once a month': { bar: '#93C1F7', label: '#1F1F1F' },
        'Do not use regularly': { bar: '#B6D6FB', label: '#373839' },
        Never: { bar: '#D2E4FA', label: '#373839' },
      },
    };
  },
  async mounted() {
    this.rawData = await d3.csv(this.dataSrc);
    this.formatData(this.question);
    this.drawChart();
  },
  methods: {
    formatData(question) {
      this.cleanData = this.rawData.filter((d) => d.country === this.country).filter((d) => d.question === question);

      let startX = 0;
      this.cleanData.forEach((d) => {
        d.startX = startX;
        d.endX = +d.percentage + d.startX;
        startX = d.endX;
      });
    },
    drawChart() {
      this.chart = d3
        .select(`#stackedbar-wrapper-${this.chartId}`)
        .select(`#stackedbar-${this.chartId}`)
        .attr('width', this.chartDim.width)
        .attr('height', this.chartDim.height)
        .style('border', '1px solid transparent')
        .call(this.responsivefy);

      this.x = d3
        .scaleLinear()
        .domain([0, 100])
        .range([0, this.chartDim.width - this.chartDim.marginX]);

      this.chart.append('g').attr('class', 'stackedbar-group').attr('transform', `translate(2, 2)`);

      this.updateChart();
    },
    updateChart() {
      const barchart = this.chart.selectAll('g.stackedbar-group');
      barchart.selectAll('g.bar-section').remove();
      barchart.selectAll('g.bar-0-value').remove();

      barchart
        .selectAll('g')
        .data(this.cleanData)
        .join('g')
        .attr('class', (d) => (+d.percentage > 0 ? 'bar-section' : 'bar-0-value'))
        .attr('data-index', (d, i) => i)
        .append('path')
        .attr('class', 'bar')
        .attr(
          'd',
          (d, i) =>
            `M${this.x(d.startX + i * 0.6)}, 0
          h${this.x(0)}
          v${this.chartDim.barHeight}
          h${-this.x(0)}Z
          `
        )
        .transition()
        .duration(800)
        .attr(
          'd',
          (d, i) =>
            `M${this.x(d.startX + i * 0.6)},0
          h${this.x(+d.percentage)}
          v${this.chartDim.barHeight}
          h${-this.x(+d.percentage)}Z
          `
        )
        .attr('fill', (d) => this.colors[d.answer].bar);

      barchart.selectAll('g.bar-0-value').remove();

      barchart
        .selectAll('path.bar')
        .on('mousemove.stackedbar', this.handleTooltip)
        .on('mouseenter.stackedbar', this.handleTooltip)
        .on('mouseleave.stackedbar', this.removeTooltip);

      // make the arc
      const lastSectionIndex = barchart.select('g.bar-section:last-of-type').attr('data-index');

      barchart
        .select('g.bar-section:last-of-type')
        .select('path.bar')
        .transition()
        .delay(700)
        .duration(0)
        .attr(
          'd',
          (d) =>
            `M${this.x(d.startX + lastSectionIndex * 0.6)}, 0
        h${this.x(+d.percentage) - this.chartDim.arc}
        q${this.chartDim.arc},0 ${this.chartDim.arc},${this.chartDim.arc}
        v${this.chartDim.barHeight - this.chartDim.arc * 2}
        q0,${this.chartDim.arc} ${-this.chartDim.arc},${this.chartDim.arc}
        h${-this.x(+d.percentage) + this.chartDim.arc}Z`
        );

      barchart
        .transition()
        .delay(700)
        .duration(0)
        .select('path.bar:first-of-type')
        .attr(
          'd',
          (d) =>
            `M${this.x(d.percentage)}, 0
          h${-this.x(+d.percentage) + this.chartDim.arc}
          q-${this.chartDim.arc},0 -${this.chartDim.arc},${this.chartDim.arc}
          v${this.chartDim.barHeight - this.chartDim.arc * 2}
          q0,${this.chartDim.arc} ${this.chartDim.arc},${this.chartDim.arc}
          h${this.x(+d.percentage) - this.chartDim.arc}Z`
        );

      // append labels
      barchart
        .selectAll('g.bar-section')
        .append('text')
        .attr('class', 'stackedbar-label')
        .text((d) => (d.percentage > 6 ? `${d.percentage}%` : ''))
        .attr('x', (d, i) => this.x(d.startX + i * 0.6) + 10)
        .attr('y', this.chartDim.barHeight / 2 + 5)
        .attr('fill', (d) => this.colors[d.answer].label);
    },
    handleTooltip(e, d) {
      d3.select(e.target).style('cursor', 'pointer');
      d3.select(e.target).transition().duration(200).attr('stroke', '#201113').attr('stroke-width', '2');

      const tooltipEl = d3.select(`#stackedbar-wrapper-${this.chartId}`).select('.chart-tooltip');
      const mouseXPadding = 20;
      const mouseYPadding = 20;
      const posX = e.layerX;
      const posY = e.layerY;
      let tooltipLeft = posX + mouseXPadding;
      let tooltipTop = posY + mouseYPadding;
      const tooltipWidth = tooltipEl.node().getBoundingClientRect().width;
      const tooltipHeight = tooltipEl.node().getBoundingClientRect().height;

      // Offisde the x1
      if (tooltipLeft + tooltipWidth >= this.chartDim.width * 0.65) {
        tooltipLeft = posX - tooltipWidth - mouseXPadding;
      }
      // Offside the y1
      if (tooltipTop + tooltipHeight >= this.chartDim.height) {
        tooltipTop = posY - tooltipHeight - mouseYPadding;
      }
      // Offside the x0
      if (tooltipLeft < 0) {
        tooltipLeft = posX + mouseXPadding;
      }
      // Offside the y0
      if (tooltipTop < 0) {
        tooltipTop = posY + mouseYPadding;
      }

      tooltipEl.style('visibility', 'visible').style('left', `${tooltipLeft}px`).style('top', `${tooltipTop}px`);

      // const posX = e.layerX;
      // const posY = e.layerY;
      // let tooltipLeft = posX;
      // const tooltipTop = posY + mousePadding;

      // // Offisde the x1
      // if (tooltipLeft + tooltipWidth >= this.chartDim.width) {
      //   tooltipLeft = posX - tooltipWidth;
      // } else {
      //   tooltipLeft = posX + mousePadding;
      // }

      this.selectedBarSection = { ...d };
    },
    removeTooltip() {
      d3.select(`#stackedbar-wrapper-${this.chartId}`).select('.chart-tooltip').style('visibility', 'hidden');
      d3.selectAll('g.bar-section path').transition().duration(200).attr('stroke', 'none');
    },
    responsivefy(svg) {
      const container = d3.select(svg.node().parentNode);
      const width = parseInt(svg.style('width'), 10);
      const height = parseInt(svg.style('height'), 10);
      const aspect = width / height;

      svg.attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMinYMid').call(this.resize, aspect);

      d3.select(window).on(`resize.${container.attr('id')}`, () => {
        this.resize(svg, aspect);
      });
    },
    resize(svg, aspect) {
      const container = d3.select(svg.node().parentNode);
      const w = parseInt(container.style('width'), 10);
      svg.attr('width', w);
      svg.attr('height', Math.round(w / aspect));
    },
  },
};
</script>
<style scoped lang="scss">
.chart-stackedbar {
  position: relative;
  width: 100%;
  @media #{map-get($media-breakpoints, 'mobile')} {
    height: 195px;
  }
}

.chart-tooltip {
  padding: 10px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.17);
  border-radius: 10px;
  width: 200px;
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  text-align: left;
  visibility: hidden;
  color: #373839;
  pointer-events: none;

  @media #{map-get($media-breakpoints, 'mobile')} {
    width: 150px;
  }
}

.mb-10 {
  margin-bottom: 10px;
}
.mb-0 {
  margin-bottom: 0px;
}
</style>

<style lang="scss">
text.stackedbar-label {
  pointer-events: none;
  cursor: pointer;
}

@media #{map-get($media-breakpoints, 'mobile')} {
  text.stackedbar-label {
    font-size: 16px;
  }
}
</style>
